import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  Close,
  CloseOutlined,
  History,
  SearchOutlined,
  StarOutlineRounded,
  StarRounded,
} from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";

import { alpha } from "@mui/material/styles";
import { blue, grey } from "@mui/material/colors";

import axios from "axios";
import { isNil } from "lodash";
import { useNavigate } from "react-router-dom";
import { losPipelineApi } from "../../../api/losPipelineApi";
import { useCachedLoanPipeline } from "../../../hooks/useCachedLoanPipeline";
import { useDrilldownBase } from "../../../pipelines/PipelineContext";
import { useReduxHook } from "../../../hooks/useReduxHook";
import { useReduxLoanDrilldownHook } from "../../../hooks/useReduxLoanDrilldownHook";
import TextInput from "../../inputs/TextInput";
import Flx from "../../layout/Flx";
import Txt from "../../typography/Txt";
import BasicModal from "../BasicModal";
import OpenInNewTabButton from "../../ui/buttons/OpenInNewTabButton";
import { nsKey } from "../../../utils/storage/partnerStorage";

const RECENT_SEARCHES_KEY = nsKey("loanRecentSearches");
const FAVORITES_STORAGE_KEY = nsKey("lo-pipeline-favorite-loans");
const MAX_RECENT_SEARCHES = 5;
const MAX_RESULTS = 20;
// Server-side search (GET /underwriting/loan_search): the backend returns `[]`
// under two characters, so don't even fire; wait for typing to settle first.
const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 250;

// Where a row opens. Search results and recents saved since the cross-pipeline
// search carry the backend's `link` ("/loan/{id}" or "/acquisition/{id}");
// entries saved before that only ever came from the pre-close list.
const loanHref = (loan) => loan?.link || `/loan/${loan?._id}`;

// Slate neutrals for the loan rows. Deliberately brand-neutral: the row palette
// is greys plus one amber favorite star, so the modal reads the same regardless
// of the partner's brand color.
const SLATE = {
  50: "#F8FAFC",
  200: "#E2E8F0",
  400: "#94A3B8",
  500: "#64748B",
  900: "#0F172A",
};

const TABS = [
  { key: "search", label: "Recent Searchs" },
  { key: "my-loans", label: "My Loans" },
  { key: "favorites", label: "Favorites" },
];

const getFavoriteLoanIds = () => {
  try {
    return JSON.parse(localStorage.getItem(FAVORITES_STORAGE_KEY)) || [];
  } catch {
    return [];
  }
};

const filterLoansByUserEmail = (loans = [], userEmail) => {
  if (!userEmail) return [];
  return loans.filter(
    (l) =>
      l?.salesperson === userEmail ||
      l?.loanCloser === userEmail ||
      l?.processor === userEmail ||
      l?.underwriter === userEmail ||
      l?.permittedUsers?.includes(userEmail),
  );
};

// Sub-line under the loan name: "#1654 · Marq Capital · Closed".
const loanLine = (l) =>
  [l?.loanNumber != null ? `#${l.loanNumber}` : null, l?.originator, l?.loanStatus]
    .filter(Boolean)
    .join(" · ");

// Third line on a search hit — the people / address the query may have matched
// on, so a borrower-name or street search shows WHY the loan came back. Only the
// search endpoint sends these; pipeline-list rows have none and skip the line.
const matchLine = (l) => {
  const names = [...(l?.borrowers || []), ...(l?.entities || [])];
  const address = l?.addresses?.[0];
  return [names.join(", "), address].filter(Boolean).join(" · ");
};

// Post-close loans live in a different collection and open at a different
// route; say so on the row, since the sub-line otherwise looks identical. Keyed
// off `link`, not `pipeline`: a pipeline-list row's `pipeline` is the loan
// document's own field (Scenario / Closed / …), and a recent entry gets that
// overlaid on top of the search endpoint's value once the pipeline loads.
const PipelineTag = ({ loan }) =>
  loanHref(loan).startsWith("/acquisition") ? (
    <Txt
      sx={{
        flexShrink: 0,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: ".04em",
        textTransform: "uppercase",
        color: SLATE[500],
        border: `1px solid ${SLATE[200]}`,
        borderRadius: "4px",
        px: 0.5,
        lineHeight: "16px",
      }}
    >
      Acquisitions
    </Txt>
  ) : null;

/**
 * One loan in any of the modal's lists. Same shape as the assign-loan dialog's
 * rows: the loan name carries the row, the number/correspondent/status sit
 * under it as a muted line, and selection is a left accent bar rather than a
 * background swap.
 */
const LoanResultRow = ({
  loan,
  selected,
  leading,
  actions,
  onClick,
  onPointerOver,
  rowProps,
}) => (
  <Flx
    ac
    gap={1.25}
    onClick={onClick}
    // mousemove, not mouseenter: after the keyboard has moved the cursor away
    // from the row the pointer is already sitting on, enter never re-fires, so
    // the mouse could no longer take back control without leaving the row first.
    onMouseEnter={onPointerOver}
    onMouseMove={onPointerOver}
    sx={{
      p: "10px 14px",
      cursor: "pointer",
      borderBottom: `0.5px solid ${SLATE[200]}`,
      borderLeft: (t) =>
        `3px solid ${selected ? t.palette.primary.main : "transparent"}`,
      background: (t) =>
        selected ? alpha(t.palette.primary.main, 0.07) : "transparent",
      "&:hover": {
        background: (t) =>
          selected ? alpha(t.palette.primary.main, 0.07) : SLATE[50],
      },
      "&:last-of-type": { borderBottom: "none" },
    }}
    {...rowProps}
  >
    {leading}
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Txt
        component="div"
        sx={{
          fontSize: 13,
          fontWeight: 600,
          color: SLATE[900],
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {loan?.loanName || `Loan #${loan?.loanNumber}`}
      </Txt>
      <Txt component="div" sx={{ fontSize: 11.5, color: SLATE[500] }}>
        {loanLine(loan)}
      </Txt>
      {matchLine(loan) && (
        <Txt
          component="div"
          sx={{
            fontSize: 11.5,
            color: SLATE[400],
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {matchLine(loan)}
        </Txt>
      )}
    </Box>
    <PipelineTag loan={loan} />
    {actions && (
      // Row actions navigate/delete on their own — never let a click through to
      // the row's own navigation.
      <Flx
        ac
        g={0.5}
        sx={{ flexShrink: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        {actions}
      </Flx>
    )}
  </Flx>
);

const OpenInNewTabAction = ({ to }) => (
  <Tooltip title="Open in new tab">
    <Box sx={{ borderRadius: "50%", ":hover": { background: blue[50] } }}>
      <OpenInNewTabButton size={15} to={to} />
    </Box>
  </Tooltip>
);

// Bordered container the lists sit in — matches the assign dialog's list frame.
const LIST_FRAME_SX = {
  border: `1px solid ${SLATE[200]}`,
  borderRadius: "9px",
  overflow: "hidden",
};

const SectionHeader = ({ icon, label, right }) => (
  <Flx jb ac sx={{ mb: 1 }}>
    <Flx ac gap={0.5}>
      {icon}
      <Txt
        component="div"
        sx={{
          fontSize: 11.5,
          fontWeight: 700,
          letterSpacing: ".04em",
          textTransform: "uppercase",
          color: SLATE[500],
        }}
      >
        {label}
      </Txt>
    </Flx>
    {right}
  </Flx>
);

const PipelineLoading = () => (
  <Flx center sx={{ py: 5 }}>
    <CircularProgress size={22} />
  </Flx>
);

const LoanSearchModal = memo(({ show, onClose, onSuccessFn }) => {
  // Cached full pipeline (shared with the correspondents screens). `[]` until
  // loaded; `isLoaded` distinguishes "empty" from "not fetched yet".
  const {
    pipeline: loanPipeline,
    isLoaded,
    ensure: ensurePipeline,
  } = useCachedLoanPipeline();
  const [recentSearches, setRecentSearches] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  // Two distinct cursors over the results, deliberately separate. `highlighted`
  // is the KEYBOARD cursor — it persists so Enter always has a target. `hovered`
  // is the MOUSE cursor and must clear the moment the pointer leaves the list;
  // when they shared one state, the last row the mouse touched stayed lit after
  // the cursor had left the modal entirely.
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [activeTab, setActiveTab] = useState("search");
  const [favoriteIds, setFavoriteIds] = useState(() => getFavoriteLoanIds());
  const { clearLoanDrilldownData } = useReduxLoanDrilldownHook();
  const { userEmail } = useReduxHook();
  const navigate = useNavigate();
  const drilldownBase = useDrilldownBase();

  const listRef = useRef(null);

  // Search results come from GET /underwriting/loan_search — server-side over
  // BOTH pipelines — not from the cached pre-close list, which could never
  // surface an acquisitions loan. `searchPending` covers the debounce window as
  // well as the request so the spinner shows from the first keystroke.
  const [searchResults, setSearchResults] = useState([]);
  const [searchPending, setSearchPending] = useState(false);
  const [searchError, setSearchError] = useState(false);

  const myLoans = useMemo(() => {
    if (isNil(loanPipeline)) return [];
    return filterLoansByUserEmail(loanPipeline, userEmail);
  }, [loanPipeline, userEmail]);

  const favoriteLoans = useMemo(() => {
    if (isNil(loanPipeline) || favoriteIds.length === 0) return [];
    return loanPipeline.filter((l) => favoriteIds.includes(l?._id));
  }, [loanPipeline, favoriteIds]);

  // Recents are read from localStorage, so they render before the pipeline has
  // been fetched — but entries saved before this stored `originator` /
  // `loanStatus` (and any that go stale) only get their sub-line once the
  // pipeline lands. Overlay the live loan when we have it.
  const recentLoans = useMemo(() => {
    if (!isLoaded) return recentSearches;
    const byId = new Map(loanPipeline.map((l) => [l._id, l]));
    return recentSearches.map((s) => {
      const live = byId.get(s._id);
      return live ? { ...s, ...live } : s;
    });
  }, [recentSearches, loanPipeline, isLoaded]);

  const query = searchTerm.trim();
  const queryTooShort = query.length > 0 && query.length < MIN_QUERY_LENGTH;

  // Debounced server search. Every keystroke restarts the timer and aborts
  // whatever request is in flight, so a slow response to "cli" can never land
  // on top of the results for "clinton". The abort also fires on close/unmount.
  useEffect(() => {
    if (!show || query.length < MIN_QUERY_LENGTH) {
      setSearchResults([]);
      setSearchPending(false);
      setSearchError(false);
      return undefined;
    }
    const controller = new AbortController();
    setSearchPending(true);
    setSearchError(false);
    const timer = setTimeout(() => {
      losPipelineApi
        .searchLoans({ q: query, limit: MAX_RESULTS, signal: controller.signal })
        .then((rows) => {
          setSearchResults(rows);
          setSearchPending(false);
        })
        .catch((err) => {
          if (axios.isCancel(err)) return; // superseded — the next query owns the UI
          setSearchResults([]);
          setSearchError(true);
          setSearchPending(false);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [show, query]);

  const filteredOptions = searchResults;

  const showSearchResults = activeTab === "search" && query.length > 0;

  // The row that reads as active: whatever the pointer is on, else the keyboard
  // cursor. Only ever one, so hover and arrow keys can't light two rows at once.
  const activeIndex = hoveredIndex != null ? hoveredIndex : highlightedIndex;

  // Reset highlight when results change / open modal
  useEffect(() => {
    setHoveredIndex(null);
    if (!showSearchResults) {
      setHighlightedIndex(-1);
      return;
    }
    setHighlightedIndex(filteredOptions.length > 0 ? 0 : -1);
  }, [showSearchResults, filteredOptions.length]);

  // Load recent searches from localStorage
  useEffect(() => {
    const loadRecentSearches = () => {
      try {
        const saved = localStorage.getItem(RECENT_SEARCHES_KEY);
        if (saved) {
          setRecentSearches(JSON.parse(saved));
        }
      } catch (error) {
        console.error("Error loading recent searches:", error);
      }
    };

    if (show) {
      loadRecentSearches();
      setSearchTerm("");
      setHighlightedIndex(-1);
      setHoveredIndex(null);
      setActiveTab("search");
      setFavoriteIds(getFavoriteLoanIds());
    }
  }, [show]);

  const saveRecentSearch = useCallback((loan) => {
    try {
      // Persist the sub-line fields too, so a reopen before the pipeline has
      // been fetched still shows a complete row rather than a bare number —
      // and `link` / `pipeline`, so an acquisitions loan reopens at
      // /acquisition/:id (it is never in the pre-close list to be looked up).
      const searchItem = {
        _id: loan._id,
        loanNumber: loan.loanNumber,
        sync_loan_id: loan.sync_loan_id,
        loanName: loan.loanName,
        originator: loan.originator,
        loanStatus: loan.loanStatus,
        link: loanHref(loan),
        pipeline: loan.pipeline,
        timestamp: Date.now(),
      };

      const saved = localStorage.getItem(RECENT_SEARCHES_KEY);
      let searches = saved ? JSON.parse(saved) : [];

      // Dedupe on `_id`: the same loan number can exist in both pipelines
      // (and, per the row keys below, more than once in one).
      searches = searches.filter((item) => item._id !== loan._id);
      searches.unshift(searchItem);
      searches = searches.slice(0, MAX_RECENT_SEARCHES);

      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(searches));
      setRecentSearches(searches);
    } catch (error) {
      console.error("Error saving recent search:", error);
    }
  }, []);

  const deleteRecentSearch = useCallback((loanId, e) => {
    e.stopPropagation();
    try {
      const saved = localStorage.getItem(RECENT_SEARCHES_KEY);
      let searches = saved ? JSON.parse(saved) : [];

      searches = searches.filter((item) => item._id !== loanId);

      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(searches));
      setRecentSearches(searches);
    } catch (error) {
      console.error("Error deleting recent search:", error);
    }
  }, []);

  const clearAllRecentSearches = useCallback(() => {
    try {
      localStorage.removeItem(RECENT_SEARCHES_KEY);
      setRecentSearches([]);
    } catch (error) {
      console.error("Error clearing recent searches:", error);
    }
  }, []);

  const handleOnClose = useCallback(() => {
    onClose(false);
  }, [onClose]);

  // Search results and recents route on the row's own `link` (the backend says
  // which collection the loan lives in). My Loans / Favorites still come from
  // the pre-close list and keep opening on the active pipeline's drilldown.
  const handleOnClick = useCallback(
    (loan, href = loanHref(loan)) => {
      if (!loan?._id) return;
      saveRecentSearch({ ...loan, link: href });

      clearLoanDrilldownData();
      navigate(`${href}/summary`);
      handleOnClose();
    },
    [navigate, handleOnClose, saveRecentSearch, clearLoanDrilldownData],
  );

  const pipelineListHref = useCallback(
    (loan) => `${drilldownBase}/${loan?._id}`,
    [drilldownBase],
  );

  // The pipeline list is only needed by My Loans / Favorites now that search is
  // server-side, so fetch it the first time one of those tabs is opened — not
  // on modal open. This component is mounted in the global header at all
  // times, and `ensure` no-ops if another consumer already cached the list.
  const needsPipeline =
    show && (activeTab === "my-loans" || activeTab === "favorites");
  useEffect(() => {
    if (needsPipeline) ensurePipeline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsPipeline]);

  const scrollItemIntoView = (index) => {
    const listEl = listRef.current;
    if (!listEl) return;
    const child = listEl.querySelector(`[data-result-index="${index}"]`);
    if (child?.scrollIntoView) {
      child.scrollIntoView({ block: "nearest" });
    }
  };

  const handleInputKeyDown = useCallback(
    (e) => {
      if (!showSearchResults) return;

      // Arrows continue from whatever row currently reads as active, so taking
      // over from the mouse steps off the row under the cursor rather than
      // jumping back to where the keyboard cursor happened to be left.
      const step = (delta) => {
        const next = Math.min(
          Math.max(activeIndex + delta, 0),
          filteredOptions.length - 1,
        );
        setHoveredIndex(null);
        setHighlightedIndex(next);
        scrollItemIntoView(next);
      };

      if (e.key === "ArrowDown") {
        e.preventDefault();
        step(1);
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        step(-1);
      }

      if (e.key === "Enter") {
        if (activeIndex >= 0 && filteredOptions[activeIndex]) {
          e.preventDefault();
          handleOnClick(filteredOptions[activeIndex]);
        }
      }

      if (e.key === "Escape") {
        e.preventDefault();
        handleOnClose();
      }
    },
    [
      showSearchResults,
      filteredOptions,
      activeIndex,
      handleOnClick,
      handleOnClose,
    ],
  );

  const handleTabChange = useCallback((tabKey) => {
    setActiveTab(tabKey);
    setHighlightedIndex(-1);
    setHoveredIndex(null);
  }, []);

  return (
    <BasicModal
      show={show}
      onClose={handleOnClose}
      title="Loan Search"
      headerContent={
        <Box>
          <Flx sx={{ p: 2, pb: 1 }} flexGrow={1} ac gap={1}>
            <TextInput
              autoFocus
              fullWidth
              size="small"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e);
                if (e.trim().length > 0 && activeTab !== "search") {
                  setActiveTab("search");
                }
              }}
              onKeyDown={handleInputKeyDown}
              sx={{ flexGrow: 1 }}
              placeholder="Search for a loan by number or name"
              InputProps={{
                startAdornment: (
                  <SearchOutlined
                    className="thin"
                    sx={{ fontSize: 18, color: SLATE[400], mr: 1 }}
                  />
                ),
              }}
              inputProps={{
                role: "combobox",
                "aria-expanded": showSearchResults,
                "aria-controls": "loan-search-results",
                "aria-activedescendant":
                  activeIndex >= 0
                    ? `loan-search-result-${activeIndex}`
                    : undefined,
              }}
            />
            <IconButton onClick={handleOnClose}>
              <CloseOutlined className="thin" />
            </IconButton>
          </Flx>
          {/* Tab bar */}
          <Flx
            ac
            sx={{
              px: 2,
              borderBottom: `1px solid ${grey[200]}`,
              gap: 0,
            }}
          >
            {TABS.map((tab) => {
              const isActive = activeTab === tab.key;
              // Counts come from the pipeline — omitted until it lands, since
              // "(0)" during the fetch reads as "you have none".
              const count =
                !isLoaded || tab.key === "search"
                  ? null
                  : tab.key === "my-loans"
                    ? myLoans.length
                    : favoriteLoans.length;
              return (
                <Box
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  sx={{
                    px: 1.5,
                    py: 1,

                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? "primary.main" : "text.secondary",
                    borderBottom: isActive
                      ? "2px solid"
                      : "2px solid transparent",
                    borderColor: isActive ? "primary.main" : "transparent",
                    cursor: "pointer",
                    userSelect: "none",
                    transition: "all 0.12s",
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    "&:hover": {
                      color: isActive ? "primary.main" : "text.primary",
                      bgcolor: isActive ? "transparent" : grey[50],
                    },
                  }}
                >
                  <Txt sx={{ fontSize: "12px" }}>{tab.label}</Txt>
                  {count != null && (
                    <Txt
                      sx={{
                        fontSize: "12px",
                        color: isActive ? "primary.main" : grey[400],
                        fontWeight: isActive ? 600 : 400,
                      }}
                    >
                      ({count})
                    </Txt>
                  )}
                </Box>
              );
            })}
          </Flx>
        </Box>
      }
      headerSx={{ pb: "0px !important" }}
      bodySx={{ p: 0 }}
      maxWidth="sm"
    >
      {/* No modal-wide loading gate: recent searches come from localStorage and
          are ready immediately, so only the pipeline-backed lists below wait on
          the fetch. Gating the whole body made a cached recents list appear
          only after a spinner it never needed. */}

      {/* ── Search Tab ── */}
      {activeTab === "search" && (
        <>
          {/* Search Results Section (keyboard navigable) */}
          {showSearchResults && (
            <Box sx={{ p: 2 }}>
              <SectionHeader
                label="Search Results"
                right={
                  !searchPending && !queryTooShort && !searchError ? (
                    <Txt variant="caption" sx={{ color: grey[500] }}>
                      {filteredOptions.length} result
                      {filteredOptions.length === 1 ? "" : "s"}
                    </Txt>
                  ) : null
                }
              />

              {searchPending ? (
                <Box sx={LIST_FRAME_SX}>
                  <PipelineLoading />
                </Box>
              ) : queryTooShort || searchError || filteredOptions.length === 0 ? (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 2,
                    color: grey[500],
                    borderTop: `1px solid ${grey[200]}`,
                  }}
                >
                  <Typography variant="body2">
                    {queryTooShort
                      ? `Type at least ${MIN_QUERY_LENGTH} characters`
                      : searchError
                        ? "Search is unavailable right now"
                        : "No loans match your search"}
                  </Typography>
                </Box>
              ) : (
                <Box
                  id="loan-search-results"
                  ref={listRef}
                  role="listbox"
                  // Drop the mouse cursor at the list boundary. Without this the
                  // last row the pointer touched stays lit for as long as the
                  // modal is open, even with the cursor well outside it.
                  onMouseLeave={() => setHoveredIndex(null)}
                  sx={{ ...LIST_FRAME_SX, maxHeight: 480, overflowY: "auto" }}
                >
                  {filteredOptions.map((opt, idx) => (
                    <LoanResultRow
                      // `_id`, not `loanNumber` — the pipeline really does carry
                      // several distinct loans under one number, and the
                      // duplicate keys that produced made React reuse the wrong
                      // row on re-render.
                      key={opt._id}
                      loan={opt}
                      selected={idx === activeIndex}
                      onPointerOver={() => setHoveredIndex(idx)}
                      onClick={() => handleOnClick(opt)}
                      actions={<OpenInNewTabAction to={loanHref(opt)} />}
                      rowProps={{
                        "data-result-index": idx,
                        id: `loan-search-result-${idx}`,
                        role: "option",
                        "aria-selected": idx === activeIndex,
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* Recent Searches Section (only when not typing) */}
          {!showSearchResults && recentLoans.length > 0 && (
            <Box sx={{ p: 2 }}>
              <SectionHeader
                icon={<History sx={{ fontSize: "15px", color: grey[600] }} />}
                label="Recent Searches"
                right={
                  <Txt
                    variant="caption"
                    sx={{
                      color: "primary.main",
                      cursor: "pointer",
                      "&:hover": { textDecoration: "underline" },
                    }}
                    onClick={clearAllRecentSearches}
                  >
                    Clear All
                  </Txt>
                }
              />

              <Box sx={LIST_FRAME_SX}>
                {recentLoans.map((search) => (
                  <LoanResultRow
                    key={search._id}
                    loan={search}
                    onClick={() => handleOnClick(search)}
                    actions={
                      <>
                        <OpenInNewTabAction to={loanHref(search)} />
                        <IconButton
                          size="small"
                          onClick={(e) =>
                            deleteRecentSearch(search._id, e)
                          }
                          sx={{ opacity: 0.6, "&:hover": { opacity: 1 } }}
                        >
                          <Close sx={{ fontSize: 15 }} />
                        </IconButton>
                      </>
                    }
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Show message if no recent searches and not typing */}
          {!showSearchResults && recentLoans.length === 0 && (
            <Box
              sx={{
                textAlign: "center",
                py: 5,
                color: grey[500],
                borderTop: `1px solid ${grey[200]}`,
              }}
            >
              <History sx={{ fontSize: 24, mb: 1, opacity: 0.5 }} />
              <Typography variant="body2">No recent searches</Typography>
            </Box>
          )}
        </>
      )}

      {/* ── Favorites Tab ── */}
      {activeTab === "favorites" && (
        <Box sx={{ p: 2 }}>
          <SectionHeader
            label="Favorited Loans"
            right={
              isLoaded ? (
                <Txt variant="caption" sx={{ color: grey[500] }}>
                  {favoriteLoans.length} loan
                  {favoriteLoans.length === 1 ? "" : "s"}
                </Txt>
              ) : null
            }
          />

          {!isLoaded ? (
            <Box sx={LIST_FRAME_SX}>
              <PipelineLoading />
            </Box>
          ) : favoriteLoans.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 4,
                color: grey[500],
                borderTop: `1px solid ${grey[200]}`,
              }}
            >
              <StarOutlineRounded sx={{ fontSize: 24, mb: 1, opacity: 0.5 }} />
              <Typography variant="body2">No favorite loans yet</Typography>
            </Box>
          ) : (
            <Box sx={{ ...LIST_FRAME_SX, maxHeight: 480, overflowY: "auto" }}>
              {favoriteLoans.map((loan) => (
                <LoanResultRow
                  key={loan._id}
                  loan={loan}
                  onClick={() => handleOnClick(loan, pipelineListHref(loan))}
                  leading={
                    <StarRounded sx={{ fontSize: 15, color: "#f59e0b" }} />
                  }
                  actions={<OpenInNewTabAction to={pipelineListHref(loan)} />}
                />
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* ── My Loans Tab ── */}
      {activeTab === "my-loans" && (
        <Box sx={{ p: 2 }}>
          <SectionHeader
            label="My Loans"
            right={
              isLoaded ? (
                <Txt variant="caption" sx={{ color: grey[500] }}>
                  {myLoans.length} loan
                  {myLoans.length === 1 ? "" : "s"}
                </Txt>
              ) : null
            }
          />

          {!isLoaded ? (
            <Box sx={LIST_FRAME_SX}>
              <PipelineLoading />
            </Box>
          ) : myLoans.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 4,
                color: grey[500],
                borderTop: `1px solid ${grey[200]}`,
              }}
            >
              <Typography variant="body2">No loans assigned to you</Typography>
            </Box>
          ) : (
            <Box sx={{ ...LIST_FRAME_SX, maxHeight: 480, overflowY: "auto" }}>
              {myLoans.map((loan) => (
                <LoanResultRow
                  key={loan._id}
                  loan={loan}
                  onClick={() => handleOnClick(loan, pipelineListHref(loan))}
                  actions={<OpenInNewTabAction to={pipelineListHref(loan)} />}
                />
              ))}
            </Box>
          )}
        </Box>
      )}
    </BasicModal>
  );
});

export default LoanSearchModal;
